import { CredentialResponse, GoogleLogin } from '@react-oauth/google';
import React, { useCallback, useState } from 'react';
import { API_CONFIG } from '../../../constants';
import { useToast } from '../../../contexts/ToastContext';
import { Shield } from 'lucide-react';

const LoginForm: React.FC = () => {
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleGoogleLogin = useCallback(
    async (credentialResponse: CredentialResponse): Promise<void> => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `${API_CONFIG.SERVER_URL}auth/google/?redirect=${encodeURIComponent(API_CONFIG.REDIRECT_URL)}`;
        form.style.display = 'none';

        Object.keys(credentialResponse).forEach((key) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = credentialResponse[
            key as keyof CredentialResponse
          ] as string;
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
      } catch (error) {
        console.error('Login error:', error);
        setIsSubmitting(false);
        showToast({
          type: 'error',
          title: 'Login Failed',
          message: 'Login failed. Please try again.',
        });
      }
    },
    [isSubmitting, showToast]
  );

  const handleGoogleLoginError = useCallback((): void => {
    showToast({
      type: 'error',
      title: 'Login Error',
      message: 'Google login failed. Please try again.',
    });
  }, [showToast]);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-lg ring-1 ring-black/5">
      {/* Top accent */}
      <div className="h-1 w-full bg-primary" aria-hidden />

      <div className="p-8 sm:p-10">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Shield className="h-6 w-6" aria-hidden />
          </div>
          <h2 className="roboto-mono text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            Welcome back
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Sign in to continue to your dashboard
          </p>
        </div>
        <div className="space-y-6">
          <div className="w-full">
            <GoogleLogin
              onSuccess={(credentialResponse: CredentialResponse) => {
                handleGoogleLogin(credentialResponse);
              }}
              onError={() => {
                handleGoogleLoginError();
              }}
              text="continue_with"
              containerProps={{
                style: {
                  height: '50px',
                },
              }}
            />
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center items-center text-xs uppercase tracking-wider">
              <span className="px-3 bg-card text-muted-foreground">
                Secure authentication
              </span>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 text-sm text-muted-foreground">
            <span className="flex items-center gap-2">
              <span
                className="w-1.5 h-1.5 rounded-full bg-success"
                aria-hidden
              />
              256-bit encryption
            </span>
            <span className="flex items-center gap-2">
              <span
                className="w-1.5 h-1.5 rounded-full bg-success"
                aria-hidden
              />
              SOC 2 compliant
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
